import { z } from 'zod';
import { SYNC_SIGNAL_KEY } from '@/constants';

export const StorageChangeEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('chat_meta_and_chat_group'),
    id: z.string().optional(),
    timestamp: z.number(),
  }),
  z.object({
    type: z.literal('chat_content'),
    id: z.string(),
    timestamp: z.number(),
  }),
  z.object({
    type: z.literal('chat_content_generation'),
    id: z.string(),
    status: z.enum(['started', 'stopped', 'abort_request']),
    timestamp: z.number(),
  }),
  z.object({
    type: z.literal('settings'),
    timestamp: z.number(),
  }),
  z.object({
    type: z.literal('binary_objects'),
    timestamp: z.number(),
  }),
  z.object({
    type: z.literal('migration'),
    timestamp: z.number(),
    // A metadata restore/import leaves volume files intact. Only an actual
    // OPFS clear retires other pages' captured File-backed volume sources.
    volumeFilesRemoved: z.literal(true).optional(),
  }),
  z.object({
    type: z.literal('naidan_rpc_registry'),
    timestamp: z.number(),
  }),
]);

export type StorageChangeEvent = z.infer<typeof StorageChangeEventSchema>;
export type ChangeType = StorageChangeEvent['type'];

export type ChangeListener = ({ event }: { event: StorageChangeEvent }) => void | Promise<void>;

// Page-local fallback is not cross-tab exclusion. Persistent model launches
// still require Web Locks; memory storage can use these serialized lanes.
const fallbackLocks = new Map<string, Promise<void>>();

export class StorageSynchronizer {
  private listeners: Set<ChangeListener> = new Set();
  private broadcastChannel: BroadcastChannel | null = null;

  constructor() {
    if ((() => {
      const t = typeof window;
      switch (t) {
      case 'undefined': return false;
      case 'object':
      case 'boolean':
      case 'string':
      case 'number':
      case 'function':
      case 'symbol':
      case 'bigint':
        return true;
      default: {
        const _ex: never = t;
        return _ex;
      }
      }
    })()) {
      // 1. LocalStorage Signal (Primary mechanism for file:// compatibility)
      window.addEventListener('storage', (e) => {
        if (e.key === SYNC_SIGNAL_KEY && e.newValue) {
          try {
            const raw = JSON.parse(e.newValue);
            const result = StorageChangeEventSchema.safeParse(raw);
            if (result.success) {
              this.emit({ event: result.data });
            } else {
              console.warn('Failed to validate storage signal:', result.error);
            }
          } catch (err) {
            console.error('Failed to parse storage signal:', err);
          }
        }
      });

      // 2. BroadcastChannel (Optimization)
      if ((() => {
        const t = typeof BroadcastChannel;
        switch (t) {
        case 'undefined': return false;
        case 'function':
        case 'object':
        case 'boolean':
        case 'string':
        case 'number':
        case 'symbol':
        case 'bigint':
          return true;
        default: {
          const _ex: never = t;
          return _ex;
        }
        }
      })()) {
        try {
          this.broadcastChannel = new BroadcastChannel('naidan_storage_sync');
          this.broadcastChannel.onmessage = (ev) => {
            const result = StorageChangeEventSchema.safeParse(ev.data);
            if (result.success) {
              this.emit({ event: result.data });
            } else {
              console.warn('Failed to validate broadcast message:', result.error);
            }
          };
        } catch (e) {
          // Ignore errors in strict environments
        }
      }
    }
  }

  /**
   * Executes a function with an exclusive lock.
   * Uses navigator.locks (Web Locks API).
   *
   * This version does not force a failure but monitors the time taken.
   * It provides callbacks to notify if lock acquisition or task execution is taking too long.
   */
  async withLock<T>({
    fn,
    lockKey,
    notifyLockWaitAfterMs = 3000,
    notifyTaskSlowAfterMs = 5000,
    onLockWait,
    onTaskSlow,
    onFinalize,
  }: {
    fn: () => Promise<T>,
    lockKey: string,
    notifyLockWaitAfterMs?: number,
    notifyTaskSlowAfterMs?: number,
    onLockWait?: () => void,
    onTaskSlow?: () => void,
    onFinalize?: () => void,
  }): Promise<T> {
    let wasSlow = false;

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      // 1. Monitor Lock Acquisition
      const lockTimer = setTimeout(() => {
        wasSlow = true;
        onLockWait?.();
      }, notifyLockWaitAfterMs);

      try {
        return await navigator.locks.request(lockKey, async () => {
          clearTimeout(lockTimer);

          // 2. Monitor Task Execution
          const taskTimer = setTimeout(() => {
            wasSlow = true;
            onTaskSlow?.();
          }, notifyTaskSlowAfterMs);

          try {
            return await fn();
          } finally {
            clearTimeout(taskTimer);
          }
        });
      } finally {
        clearTimeout(lockTimer);
        if (wasSlow) {
          onFinalize?.();
        }
      }
    } else {
      const previous = fallbackLocks.get(lockKey) ?? Promise.resolve();
      const released = Promise.withResolvers<void>();
      const tail = previous.then(() => released.promise);
      fallbackLocks.set(lockKey, tail);
      await previous;
      try {
        return await fn();
      } finally {
        released.resolve();
        if (fallbackLocks.get(lockKey) === tail) {
          fallbackLocks.delete(lockKey);
        }
      }
    }
  }

  /**
   * Notifies other tabs of a change.
   */
  notify({ event }: { event: StorageChangeEvent }): void {
    // 1. LocalStorage Signal
    try {
      localStorage.setItem(SYNC_SIGNAL_KEY, JSON.stringify(event));
    } catch (e) {
      console.warn('Failed to signal via localStorage:', e);
    }

    // 2. BroadcastChannel Signal
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
      } catch (e) {
        // ignore
      }
    }
  }

  subscribe({ listener }: { listener: ChangeListener }) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit({ event }: { event: StorageChangeEvent }) {
    this.listeners.forEach(l => l({ event }));
  }
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
